import type { Oznaka } from "@/lib/status";
import { PropertyList, PropertyItem } from "@/components/ui/property-list";
import axios from "axios";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { SkeletonTablica } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { procitajGreske } from "@/lib/greske";
import {
  deaktivirajPosebnuPotrebu,
  dohvatiHtzVelicine,
  dohvatiPosebnePotrebe,
  dohvatiVrstePosebnihPotreba,
  spremiHtzVelicine,
  spremiPosebnuPotrebu,
  type HtzVelicine,
  type HtzVelicineInput,
  type PosebnaPotreba,
  type PosebnaPotrebaInput,
  type VrstaPosebnePotrebe,
} from "@/lib/radnici";

const PRAZNE_VELICINE: HtzVelicineInput = {
  gornji_dio: null,
  donji_dio: null,
  obuca: null,
  kaciga: null,
  rukavice: null,
  zastitni_pojas: null,
  prsluk: null,
};

const POLJA_VELICINA: [keyof HtzVelicineInput, string][] = [
  ["gornji_dio", "Odjeća — gornji dio"],
  ["donji_dio", "Odjeća — donji dio"],
  ["obuca", "Obuća"],
  ["kaciga", "Kaciga"],
  ["rukavice", "Rukavice"],
  ["zastitni_pojas", "Zaštitni pojas"],
  ["prsluk", "Prsluk"],
];

const PRAZNA_POTREBA: PosebnaPotrebaInput = {
  vrsta_id: "",
  opis: null,
  vrijednost: null,
  napomena: null,
  aktivna: true,
};

const control =
  "min-h-11 h-[var(--spacing-control)] rounded-[var(--radius-control)] border border-[var(--color-input-border)] bg-white px-3";

function poruka(error: unknown): string {
  return procitajGreske(error).opcenito[0] ?? "Spremanje nije uspjelo.";
}

export function OpremaVelicineTab({ radnikId, onSizeState, onSaved }: { radnikId: string; onSizeState?: (badge: Oznaka) => void; onSaved?: () => void }) {
  const { can } = useAuth();
  const [htz, setHtz] = useState<HtzVelicine | null>(null);
  const [velicine, setVelicine] = useState<HtzVelicineInput>(PRAZNE_VELICINE);
  const [potrebe, setPotrebe] = useState<PosebnaPotreba[]>([]);
  const [vrste, setVrste] = useState<VrstaPosebnePotrebe[]>([]);
  const [urediVelicine, setUrediVelicine] = useState(false);
  const [potreba, setPotreba] = useState<PosebnaPotrebaInput>(PRAZNA_POTREBA);
  const [urediPotrebuId, setUrediPotrebuId] = useState<string | null>(null);
  const [prikaziPotrebu, setPrikaziPotrebu] = useState(false);
  const [ucitava, setUcitava] = useState(true);
  const [sprema, setSprema] = useState(false);
  const [greska, setGreska] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    Promise.all([
      dohvatiHtzVelicine(radnikId, controller.signal).catch((error: unknown) => {
        if (axios.isAxiosError(error) && error.response?.status === 404) return null;
        throw error;
      }),
      dohvatiPosebnePotrebe(radnikId, controller.signal),
      dohvatiVrstePosebnihPotreba(radnikId, controller.signal),
    ])
      .then(([noveVelicine, novePotrebe, noveVrste]) => {
        if (controller.signal.aborted) return;
        setHtz(noveVelicine);
        setVelicine(noveVelicine ?? PRAZNE_VELICINE);
        setPotrebe(novePotrebe);
        setVrste(noveVrste);
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) setGreska(poruka(error));
      })
      .finally(() => {
        if (!controller.signal.aborted) setUcitava(false);
      });
    return () => controller.abort();
  }, [radnikId]);

  useEffect(() => {
    if (ucitava) return;
    const missing = POLJA_VELICINA.filter(([key]) => !htz?.[key]).length;
    onSizeState?.(greska ? { ton: "idle", tekst: "Veličine nisu dostupne" } : { ton: missing ? "bad" : "ok", tekst: missing ? `Nedostaje ${missing} veličina – dopuni` : "Veličine upisane" });
  }, [htz, ucitava, greska, onSizeState]);

  async function spremiVelicine(): Promise<void> {
    setSprema(true);
    setGreska(null);
    try {
      const spremljeno = await spremiHtzVelicine(radnikId, velicine, htz !== null);
      setHtz(spremljeno);
      setVelicine(spremljeno);
      setUrediVelicine(false);
      onSaved?.();
    } catch (error) {
      setGreska(poruka(error));
    } finally {
      setSprema(false);
    }
  }

  function zapocniPotrebu(zapis?: PosebnaPotreba): void {
    setPotreba(zapis ? { ...zapis } : PRAZNA_POTREBA);
    setUrediPotrebuId(zapis?.id ?? null);
    setPrikaziPotrebu(true);
  }

  async function spremiPotrebu(): Promise<void> {
    setSprema(true);
    setGreska(null);
    try {
      const spremljeno = await spremiPosebnuPotrebu(
        radnikId,
        potreba,
        urediPotrebuId ?? undefined,
      );
      setPotrebe((stare) =>
        urediPotrebuId
          ? stare.map((zapis) => (zapis.id === spremljeno.id ? spremljeno : zapis))
          : [spremljeno, ...stare],
      );
      setPrikaziPotrebu(false);
      setUrediPotrebuId(null);
      setPotreba(PRAZNA_POTREBA);
    } catch (error) {
      setGreska(poruka(error));
    } finally {
      setSprema(false);
    }
  }

  async function deaktiviraj(zapis: PosebnaPotreba): Promise<void> {
    setSprema(true);
    setGreska(null);
    try {
      const spremljeno = await deaktivirajPosebnuPotrebu(radnikId, zapis.id);
      setPotrebe((stare) =>
        stare.map((stavka) => (stavka.id === spremljeno.id ? spremljeno : stavka)),
      );
    } catch (error) {
      setGreska(poruka(error));
    } finally {
      setSprema(false);
    }
  }

  if (ucitava) return <SkeletonTablica redaka={3} />;
  if (greska && !htz && potrebe.length === 0 && vrste.length === 0) {
    return <EmptyState naslov="Oprema nije dostupna" objasnjenje={greska} />;
  }

  const mozeVelicine = htz ? can("znr", "update") : can("znr", "create");

  return (
    <div className="flex flex-col gap-6">
      {greska ? <p role="alert" className="text-[var(--color-status-bad-fg)]">{greska}</p> : null}
      <section className="avm-surface rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--color-border)] pb-3">
          <div>
            <h2 className="font-semibold">Veličine i posebne potrebe</h2>
            <p className="text-sm text-[var(--color-muted-foreground)]">Jedan aktualni zapis po radniku.</p>
          </div>
          {mozeVelicine ? (
            <Button variant="outline" onClick={() => setUrediVelicine((value) => { if (!value) setVelicine(htz ?? PRAZNE_VELICINE); return !value; })}>
              {urediVelicine ? "Odustani" : htz ? "Uredi veličine" : "Unesi veličine"}
            </Button>
          ) : null}
        </div>
        <div className="mt-4">{urediVelicine ? <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{POLJA_VELICINA.map(([ime, oznaka]) => <label key={ime} className="flex flex-col gap-2"><span className="oznaka">{oznaka}</span><input className={control} maxLength={40} value={velicine[ime] ?? ""} onChange={event => setVelicine({ ...velicine, [ime]: event.target.value || null })} /></label>)}</div> : <PropertyList>{POLJA_VELICINA.map(([ime, oznaka]) => <PropertyItem key={ime} label={oznaka} missing={!htz?.[ime]} onAdd={mozeVelicine ? () => setUrediVelicine(true) : undefined}>{htz?.[ime]}</PropertyItem>)}</PropertyList>}</div>
        {urediVelicine ? <Button className="mt-5" disabled={sprema} onClick={() => void spremiVelicine()}>{sprema ? "Spremanje…" : "Spremi veličine"}</Button> : null}
      </section>

      <section className="avm-surface rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--color-border)] pb-3">
          <h2 className="font-semibold">Posebne potrebe za opremom</h2>
          {can("znr", "create") ? <Button onClick={() => zapocniPotrebu()}>Dodaj potrebu</Button> : null}
        </div>
        {prikaziPotrebu ? (
          <div className="mt-4 grid gap-4 rounded-[var(--radius-card)] bg-[var(--color-muted)] p-4 sm:grid-cols-2">
            <label className="flex flex-col gap-2"><span className="oznaka">Vrsta</span><select className={control} value={potreba.vrsta_id} onChange={(event) => setPotreba({ ...potreba, vrsta_id: event.target.value })}><option value="">— odaberite —</option>{vrste.map((vrsta) => <option key={vrsta.id} value={vrsta.id}>{vrsta.naziv}</option>)}</select></label>
            <label className="flex flex-col gap-2"><span className="oznaka">Vrijednost / veličina</span><input className={control} maxLength={120} value={potreba.vrijednost ?? ""} onChange={(event) => setPotreba({ ...potreba, vrijednost: event.target.value || null })} /></label>
            <label className="flex flex-col gap-2"><span className="oznaka">Opis</span><textarea className="min-h-20 rounded-[var(--radius-control)] border border-[var(--color-input-border)] bg-white p-3" value={potreba.opis ?? ""} onChange={(event) => setPotreba({ ...potreba, opis: event.target.value || null })} /></label>
            <label className="flex flex-col gap-2"><span className="oznaka">Napomena</span><textarea className="min-h-20 rounded-[var(--radius-control)] border border-[var(--color-input-border)] bg-white p-3" value={potreba.napomena ?? ""} onChange={(event) => setPotreba({ ...potreba, napomena: event.target.value || null })} /></label>
            <div className="flex gap-3 sm:col-span-2"><Button disabled={sprema || !potreba.vrsta_id} onClick={() => void spremiPotrebu()}>{sprema ? "Spremanje…" : "Spremi potrebu"}</Button><Button variant="outline" onClick={() => setPrikaziPotrebu(false)}>Odustani</Button></div>
          </div>
        ) : null}
        <div className="mt-4 flex flex-col gap-3">
          {potrebe.length === 0 ? <p className="text-[var(--color-muted-foreground)]">Nema evidentiranih posebnih potreba.</p> : potrebe.map((zapis) => {
            const vrsta = vrste.find((stavka) => stavka.id === zapis.vrsta_id);
            return <article key={zapis.id} className="avm-surface grid gap-3 rounded-[var(--radius-card)] border border-[var(--color-border)] p-4 md:grid-cols-[1fr_1fr_auto]">
              <div><p className="font-medium">{vrsta?.naziv ?? "Nepoznata vrsta"}</p><p className="text-sm text-[var(--color-muted-foreground)]">{zapis.opis ?? "Bez opisa"}</p></div>
              <div><p>{vrsta?.vrijednost_oznaka ?? "Vrijednost"}: {zapis.vrijednost ?? "—"}</p><p className="text-sm text-[var(--color-muted-foreground)]">{zapis.napomena ?? "Bez napomene"}</p><p className="text-sm">{zapis.aktivna ? "Aktivno" : "Neaktivno"}</p></div>
              {can("znr", "update") && zapis.aktivna ? <div className="flex flex-wrap gap-2"><Button variant="outline" onClick={() => zapocniPotrebu(zapis)}>Uredi</Button><Button variant="outline" disabled={sprema} onClick={() => void deaktiviraj(zapis)}>Deaktiviraj</Button></div> : null}
            </article>;
          })}
        </div>
      </section>
    </div>
  );
}
